import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { SelectInput } from '@/components/form/baseInputs/SelectInput/SelectInput';
import { getOptionsWithGroupLabels } from '@/components/form/baseInputs/SelectInput/SelectInput.utils';
import { STATE_CODE_TO_NAME } from '@/constants/states';
import { AssetV2Fragment } from '@/modules/assets/graphql/Asset.generated';
import {
  AssetTaxSitusFormValue,
  TAX_SITUS_GRANTOR_PREFIX,
  TAX_SITUS_STATE_PREFIX,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import {
  encodeAssetTaxSitusFormValue,
  getOwnerAwareDefaultTaxSitus,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { StateCode } from '@/types/schema';

export type AssetForTaxSitus = Pick<
  AssetV2Fragment,
  'id' | 'assetStatic' | 'class'
>;

interface IntegratedAssetTaxSitusCellProps<T extends AssetForTaxSitus> {
  asset: T;
  onChange: (
    asset: T,
    nextSitus: AssetTaxSitusFormValue
  ) => void | Promise<void>;
  disabled?: boolean;
  // Host entity's principal client IDs. Threaded in so that the displayed
  // default for assets with no persisted situs matches the spec's owner-aware
  // rule (single owner → owner residency; joint/multi → primary grantor).
  // Pass undefined to fall back to the primary-grantor default.
  ownerPrincipalIds?: string[];
}

export function IntegratedAssetTaxSitusCell<T extends AssetForTaxSitus>({
  asset,
  onChange,
  disabled,
  ownerPrincipalIds,
}: IntegratedAssetTaxSitusCellProps<T>) {
  const persistedValue = encodeAssetTaxSitusFormValue(asset.assetStatic);
  const [optimisticValue, setOptimisticValue] =
    useState<AssetTaxSitusFormValue | null>(null);

  const { primaryClients } = useUnguardedHouseholdDetailsContext();

  const sortedPrimaryClients = useMemo(
    () => orderBy(primaryClients ?? [], [(c) => c.createdAt, (c) => c.id]),
    [primaryClients]
  );

  const defaultGrantorValue: AssetTaxSitusFormValue =
    getOwnerAwareDefaultTaxSitus({
      primaryClients: sortedPrimaryClients,
      ownerPrincipalIds,
    });
  const value =
    optimisticValue ??
    (persistedValue === '' ? defaultGrantorValue : persistedValue);

  const groupedOptions = useMemo(() => {
    const grantorOptions: SelectInputOption[] = sortedPrimaryClients.map(
      (client) => {
        const statePrefix = client.stateCode ? `${client.stateCode} - ` : '';
        return {
          value: `${TAX_SITUS_GRANTOR_PREFIX}${client.id}`,
          display: `${statePrefix}(${client.displayGivenName}'s resident state)`,
        };
      }
    );
    const stateCodeOptions: SelectInputOption[] = (
      Object.keys(STATE_CODE_TO_NAME) as StateCode[]
    ).map((stateCode) => ({
      value: `${TAX_SITUS_STATE_PREFIX}${stateCode}`,
      display: stateCode,
    }));
    return getOptionsWithGroupLabels([
      { label: 'State of domicile', options: grantorOptions },
      { label: null, options: stateCodeOptions },
    ]);
  }, [sortedPrimaryClients]);

  const showEmptyValue = !value;

  return (
    <SelectInput
      label="State"
      hideLabel
      value={value}
      options={groupedOptions}
      disabled={disabled}
      showEmptyValue={showEmptyValue}
      onChange={(event) => {
        const nextValue = (event.target.value ?? '') as AssetTaxSitusFormValue;
        setOptimisticValue(nextValue);
        void Promise.resolve(onChange(asset, nextValue)).finally(() => {
          setOptimisticValue(null);
        });
      }}
    />
  );
}
